import { LifecycleConnection } from "server/models"
import {
    LoadLifecycleConnectionsConnectedTo,
    UpdateManyLifecycleConnections,
} from "client/store"

import { top, bottom, left, right } from "../const"
import { PhaseGui, Side, GetPhaseGui } from "./phaseGui"
import { PhaseTitleHeight } from "../styled"


export const UpdateConnectionPoints = (
    phaseId: number,
    phaseName: string,
    phasePosition: { x: number, y: number },
) => {
    const titleHeight = PhaseTitleHeight(phaseName)
    const gui = GetPhaseGui(titleHeight)

    return LoadLifecycleConnectionsConnectedTo(phaseId)
    .then((connections) => {
        const newConnections = CalculateConnectionPoints(phaseId, phasePosition, gui, connections)
        return UpdateManyLifecycleConnections(newConnections)
    })
}

export const CalculateConnectionPoints = (
    phaseId: number,
    phasePosition: { x: number, y: number },
    phaseGui: PhaseGui,
    connections: readonly LifecycleConnection[],
) => {
    const newConnections: LifecycleConnection[] = []

    connections.forEach((connection) => {
        const {
            startPhaseId,
            endPhaseId,
            startPhaseSide,
            endPhaseSide,
            pointsArray,
        } = connection

        if (startPhaseId === phaseId || endPhaseId === phaseId) {
            const startX = pointsArray[0]
            const startY = pointsArray[1]
            const endX = pointsArray[2]
            const endY = pointsArray[3]

            let newPointsArray: number[]

            // gui point is relative to the phase rectangle
            if (startPhaseId === phaseId) {
                const newStartPoint = phaseGui[startPhaseSide as Side]
                const { x, y } = newStartPoint
                newPointsArray = [
                    x + phasePosition.x,
                    y + phasePosition.y,
                    endX,
                    endY,
                ]
            }
            if (endPhaseId === phaseId) {
                const newEndPoint = phaseGui[endPhaseSide as Side]
                const { x, y } = newEndPoint
                newPointsArray = [
                    startX,
                    startY,
                    x + phasePosition.x,
                    y + phasePosition.y,
                ]
            }

            newConnections.push({
                ...connection,
                points: JSON.stringify(newPointsArray),
                pointsArray: newPointsArray,
            })
        } else {
            newConnections.push(connection)
        }
    })

    return newConnections
}

export const GetOrthogonalPoints = (
    points: readonly number[],
    startPhaseSide: Side,
    endPhaseSide: Side,
) => {
    const startX = points[0]
    const startY = points[1]
    const endX = points[2]
    const endY = points[3]

    // how far from the start the middle of the line
    const midOffset = 2.5

    // small offset from the edge of the phase to start bending
    const sideOffset = 20

    const resultPoints: number[] = []
    resultPoints.push(startX, startY)

    const xMid = startX + (endX - startX) / midOffset
    const yMid = startY + (endY - startY) / midOffset

    if (startPhaseSide === top && endPhaseSide === bottom) {
        const yMax = startY - sideOffset
        const y = Math.min(yMax, yMid)
        resultPoints.push(startX, y)
        resultPoints.push(endX, y)
    }

    if (startPhaseSide === bottom && endPhaseSide === top) {
        const yMin = startY + sideOffset
        const y = Math.max(yMin, yMid)
        resultPoints.push(startX, y)
        resultPoints.push(endX, y)
    }

    if (startPhaseSide === right && endPhaseSide === left) {
        const xMin = startX + sideOffset
        const x = Math.max(xMin, xMid)
        resultPoints.push(x, startY)
        resultPoints.push(x, endY)
    }

    if (startPhaseSide === left && endPhaseSide === right) {
        const xMax = startX - sideOffset
        const x = Math.min(xMax, xMid)
        resultPoints.push(x, startY)
        resultPoints.push(x, endY)
    }

    if (startPhaseSide === top && endPhaseSide === top) {
        const yMin = Math.min(startY, endY)
        const y = yMin - sideOffset
        resultPoints.push(startX, y)
        resultPoints.push(endX, y)
    }

    if (startPhaseSide === bottom && endPhaseSide === bottom) {
        const yMax = Math.max(startY, endY)
        const y = yMax + sideOffset
        resultPoints.push(startX, y)
        resultPoints.push(endX, y)
    }

    if (startPhaseSide === right && endPhaseSide === right) {
        const xMax = Math.max(startX, endX)
        const x = xMax + sideOffset
        resultPoints.push(x, startY)
        resultPoints.push(x, endY)
    }

    if (startPhaseSide === left && endPhaseSide === left) {
        const xMin = Math.min(startX, endX)
        const x = xMin - sideOffset
        resultPoints.push(x, startY)
        resultPoints.push(x, endY)
    }

    if ([top, bottom].includes(startPhaseSide) && [right, left].includes(endPhaseSide)) {
        resultPoints.push(startX, endY)
    }

    if ([right, left].includes(startPhaseSide) && [top, bottom].includes(endPhaseSide)) {
        resultPoints.push(endX, startY)
    }

    resultPoints.push(endX, endY)

    return resultPoints
}
